import {
    CancelReservationModel,
    CancelReservationResult,
    ConfirmRequestResult,
    CreateReservationResult,
    ArchiveRequestResult,
    DeclineRequestModel,
    RequestClosureResult,
    ReservationDTO,
    ReservationListFilter,
    Reservations,
    SaveRequestResult,
    UpdateConfirmedResult,
    UpdateSeatedResult,
    UpdateReservationModel,
} from 'api/controllers/reservations';
import i18n from 'i18n-js';

import { isReservationFilterActive } from 'helpers/reservationHelper';
import { AppThunkAction, AppThunkActionAsync } from '../index';
import globalStore from '../globalStore';
import { ReservationsAction } from './actions';

let requestsLoad: { restaurantId: string; promise: Promise<boolean> } | null = null;
let requestsViewLoad = 0;
let requestsRevision = 0;
let availabilityLoad = 0;
let activeLoad = 0;
let activeRevision = 0;
const cancellationRequests = new Map<string, Promise<CancelReservationResult>>();
type RequestClosureOperation = 'decline' | 'cancel';
const requestClosures = new Map<
    string,
    { operation: RequestClosureOperation; promise: Promise<RequestClosureResult> }
>();
const requestRefreshes = new Map<string, Promise<boolean>>();

async function getCurrentReservation(id: string): Promise<ReservationDTO> {
    let revision = activeRevision;
    while (true) {
        const reservation = await Reservations.getById(id);
        if (revision === activeRevision) {
            return reservation;
        }
        revision = activeRevision;
    }
}

function saveFinalStatus(
    id: string,
    save: (id: string) => Promise<ReservationDTO>
): AppThunkActionAsync<ReservationsAction, void> {
    return async (dispatch, getState) => {
        try {
            const reservation = await save(id);
            actionCreators.applyRealtimeUpdate(reservation)(dispatch, getState);
        } catch (error) {
            try {
                const current = await getCurrentReservation(id);
                actionCreators.applyRealtimeUpdate(current)(dispatch, getState);
            } catch (refreshError) {
                globalStore.actionCreators.showToaster('error', refreshError)(dispatch);
            }
            throw error;
        }
    };
}

function closeRequest(
    id: string,
    operation: RequestClosureOperation,
    save: () => Promise<RequestClosureResult>
): AppThunkActionAsync<ReservationsAction, RequestClosureResult> {
    return (dispatch, getState) => {
        const pending = requestClosures.get(id);
        if (pending) {
            return pending.operation === operation
                ? pending.promise
                : Promise.reject(new Error('A different request closure is already pending.'));
        }
        if (getState?.().reservations.requestRefresh?.[id]) {
            return Promise.resolve({ status: 'alreadyHandled', refreshFailed: true });
        }
        const restaurantId = getState?.().user.currentRestaurantId;
        const isCurrent = () => getState?.().user.currentRestaurantId === restaurantId;
        dispatch({ type: 'RESERVATION_REQUEST_CLOSURE_PENDING', id, operation, isPending: true });
        const request = (async () => {
            try {
                const result = await save();
                if (!isCurrent()) return result;
                if (result.status === 'alreadyHandled') {
                    dispatch({ type: 'RESERVATION_REQUEST_REFRESH', id, status: 'loading' });
                    globalStore.actionCreators.showToaster(
                        'error',
                        i18n.t('reservations.alreadyHandled')
                    )(dispatch);
                    const refreshed = await actionCreators.refreshHandledRequest(id)(
                        dispatch,
                        getState
                    );
                    return { ...result, refreshFailed: !refreshed };
                } else if (isCurrent()) {
                    dispatch({ type: 'RESERVATION_REQUEST_DIALOG', id: null });
                    actionCreators.applyRealtimeUpdate(result.reservation)(dispatch, getState);
                }
                return result;
            } finally {
                if (isCurrent()) {
                    dispatch({
                        type: 'RESERVATION_REQUEST_CLOSURE_PENDING',
                        id,
                        operation,
                        isPending: false,
                    });
                }
            }
        })();
        requestClosures.set(id, { operation, promise: request });
        const clear = () => {
            if (requestClosures.get(id)?.promise === request) requestClosures.delete(id);
        };
        request.then(clear, clear);
        return request;
    };
}

export const actionCreators = {
    refreshHandledRequest:
        (id: string): AppThunkActionAsync<ReservationsAction, boolean> =>
        (dispatch, getState) => {
            const pending = requestRefreshes.get(id);
            if (pending) return pending;
            const restaurantId = getState?.().user.currentRestaurantId;
            const isCurrent = () => getState?.().user.currentRestaurantId === restaurantId;
            dispatch({ type: 'RESERVATION_REQUEST_REFRESH', id, status: 'loading' });
            const request = (async () => {
                let reservation: ReservationDTO;
                try {
                    reservation = await getCurrentReservation(id);
                } catch (error) {
                    console.error('Failed to refresh a handled reservation request:', error);
                    if (isCurrent() && getState?.().reservations.requestRefresh?.[id]) {
                        dispatch({ type: 'RESERVATION_REQUEST_REFRESH', id, status: 'failed' });
                    }
                    return false;
                }
                if (!isCurrent()) return false;
                actionCreators.applyRealtimeUpdate(reservation)(dispatch, getState);
                return true;
            })();
            requestRefreshes.set(id, request);
            const clear = () => {
                if (requestRefreshes.get(id) === request) requestRefreshes.delete(id);
            };
            request.then(clear, clear);
            return request;
        },

    getRequests:
        (restaurantId: string): AppThunkActionAsync<ReservationsAction, boolean> =>
        (dispatch, getState) => {
            const revision = requestsRevision;
            const isStillCurrent = () => getState?.().user.currentRestaurantId === restaurantId;
            const promise = Reservations.getRequests()
                .then((requests) => {
                    if (!isStillCurrent()) {
                        return false;
                    }
                    if (revision !== requestsRevision) {
                        return actionCreators.getRequests(restaurantId)(dispatch, getState);
                    }
                    dispatch({
                        type: 'RESERVATION_REQUESTS_SET',
                        restaurantId,
                        requests,
                    });
                    return true;
                })
                .catch((e) => {
                    if (isStillCurrent()) {
                        globalStore.actionCreators.showToaster('error', e)(dispatch);
                    }
                    return false;
                })
                .finally(() => {
                    if (requestsLoad?.promise === promise) {
                        requestsLoad = null;
                    }
                });
            requestsLoad = { restaurantId, promise };
            return promise;
        },

    resync:
        (restaurantId: string): AppThunkAction<ReservationsAction> =>
        (dispatch, getState) => {
            if (!getState || getState().user.currentRestaurantId !== restaurantId) return;
            actionCreators.getRequests(restaurantId)(dispatch, getState);
            activeRevision++;
            const { active } = getState().reservations;
            if (active.day !== undefined && !active.isLoading) {
                actionCreators.getActiveReservations(active.day, active.search, {
                    keepPrevious: true,
                    isList: true,
                })(dispatch, getState);
            }
        },

    ensureRequests:
        (restaurantId: string): AppThunkActionAsync<ReservationsAction, boolean> =>
        (dispatch, getState) => {
            if (getState?.().reservations.requestsRestaurantId === restaurantId) {
                return Promise.resolve(true);
            }
            if (requestsLoad?.restaurantId === restaurantId) {
                return requestsLoad.promise;
            }
            return actionCreators.getRequests(restaurantId)(dispatch, getState);
        },

    selectRequest: (id: string | null): ReservationsAction => ({
        type: 'RESERVATION_REQUEST_SELECT',
        id,
    }),

    selectActive: (id: string | null): ReservationsAction => ({
        type: 'RESERVATION_ACTIVE_SELECT',
        id,
    }),

    getActiveReservations:
        (
            day: string | null,
            search = '',
            options: { keepPrevious?: boolean; isList?: boolean } = {}
        ): AppThunkActionAsync<ReservationsAction> =>
        (dispatch, getState) => {
            const load = ++activeLoad;
            const revision = activeRevision;
            const restaurantId = getState?.().user.currentRestaurantId;
            const isCurrent = () =>
                load === activeLoad && restaurantId === getState?.().user.currentRestaurantId;
            dispatch({
                type: 'RESERVATION_ACTIVE_LOAD',
                day,
                search,
                keepPrevious: !!options.keepPrevious,
            });
            return Reservations.getActiveReservations(day, search)
                .then((reservations) => {
                    if (!isCurrent()) {
                        return;
                    }
                    if (revision !== activeRevision) {
                        return actionCreators.getActiveReservations(day, search, {
                            ...options,
                            keepPrevious: true,
                        })(dispatch, getState);
                    }
                    dispatch({ type: 'RESERVATION_ACTIVE_SET', day, search, reservations });
                })
                .catch((e) => {
                    if (isCurrent()) {
                        dispatch({ type: 'RESERVATION_ACTIVE_FAIL' });
                        globalStore.actionCreators.showToaster(
                            'error',
                            options.isList ? i18n.t('reservations.filters.loadError') : e
                        )(dispatch);
                    }
                });
        },

    loadRequestsView:
        (filter: ReservationListFilter): AppThunkAction<ReservationsAction> =>
        (dispatch, getState) => {
            const load = ++requestsViewLoad;
            if (!isReservationFilterActive(filter)) {
                dispatch({ type: 'RESERVATION_REQUESTS_VIEW_SET', filter: null, requests: [] });
                return;
            }
            const revision = requestsRevision;
            const restaurantId = getState?.().user.currentRestaurantId;
            const isCurrent = () =>
                load === requestsViewLoad && restaurantId === getState?.().user.currentRestaurantId;
            dispatch({ type: 'RESERVATION_REQUESTS_VIEW_LOAD' });
            Reservations.getRequests(filter)
                .then((requests) => {
                    if (!isCurrent()) {
                        return;
                    }
                    if (revision !== requestsRevision) {
                        actionCreators.loadRequestsView(filter)(dispatch, getState);
                        return;
                    }
                    dispatch({ type: 'RESERVATION_REQUESTS_VIEW_SET', filter, requests });
                })
                .catch(() => {
                    if (isCurrent()) {
                        dispatch({ type: 'RESERVATION_REQUESTS_VIEW_FAIL' });
                        globalStore.actionCreators.showToaster(
                            'error',
                            i18n.t('reservations.filters.loadError')
                        )(dispatch);
                    }
                });
        },

    getTablesAvailability:
        (
            start: string,
            durationMinutes: number,
            excludeReservationId: string | null
        ): AppThunkAction<ReservationsAction> =>
        (dispatch) => {
            const load = ++availabilityLoad;
            const key = [start, durationMinutes, excludeReservationId].join('|');
            dispatch({ type: 'RESERVATION_AVAILABILITY_LOAD', key });
            Reservations.getTablesAvailability(start, durationMinutes, excludeReservationId)
                .then((tables) => {
                    if (load === availabilityLoad) {
                        dispatch({ type: 'RESERVATION_AVAILABILITY_SET', key, tables });
                    }
                })
                .catch((e) => {
                    if (load === availabilityLoad) {
                        globalStore.actionCreators.showToaster('error', e)(dispatch);
                    }
                });
        },

    clearTablesAvailability: (): ReservationsAction => ({
        type: 'RESERVATION_AVAILABILITY_CLEAR',
    }),

    saveRequest:
        (
            id: string,
            model: UpdateReservationModel
        ): AppThunkActionAsync<ReservationsAction, SaveRequestResult> =>
        async (dispatch, getState) => {
            const changeRevision = getState?.().reservations.changeRevision;
            const result = await Reservations.saveRequest(id, model);
            if (result.status === 'saved') {
                let reservation = result.reservation;
                if (getState && getState().reservations.changeRevision !== changeRevision) {
                    try {
                        reservation = await getCurrentReservation(id);
                    } catch (error) {
                        globalStore.actionCreators.showToaster('error', error)(dispatch);
                        return result;
                    }
                }
                actionCreators.applyRealtimeUpdate(reservation)(dispatch, getState);
            } else {
                activeRevision++;
                requestsRevision++;
                dispatch({ type: 'RESERVATION_REQUEST_REMOVED', id });
            }
            return result;
        },

    confirmRequest:
        (
            id: string,
            model: UpdateReservationModel
        ): AppThunkActionAsync<ReservationsAction, ConfirmRequestResult> =>
        async (dispatch) => {
            const result = await Reservations.confirmRequest(id, model);
            if (result.status === 'confirmed') {
                activeRevision++;
                requestsRevision++;
                dispatch({ type: 'RESERVATION_CONFIRMED', reservation: result.reservation });
            } else if (result.status === 'alreadyHandled') {
                activeRevision++;
                requestsRevision++;
                dispatch({ type: 'RESERVATION_REQUEST_REMOVED', id });
            }
            return result;
        },

    declineRequest: (
        id: string,
        model: DeclineRequestModel
    ): AppThunkActionAsync<ReservationsAction, RequestClosureResult> =>
        closeRequest(id, 'decline', () => Reservations.declineRequest(id, model)),

    cancelRequest: (
        id: string,
        model: CancelReservationModel
    ): AppThunkActionAsync<ReservationsAction, RequestClosureResult> =>
        closeRequest(id, 'cancel', () => Reservations.cancelRequest(id, model)),

    archiveRequest:
        (id: string): AppThunkActionAsync<ReservationsAction, ArchiveRequestResult> =>
        async (dispatch, getState) => {
            const result = await Reservations.archiveRequest(id);
            if (result === 'archived') {
                activeRevision++;
                requestsRevision++;
                dispatch({ type: 'RESERVATION_REQUEST_REMOVED', id });
            } else if (getState) {
                await actionCreators.getRequests(getState().user.currentRestaurantId)(
                    dispatch,
                    getState
                );
            }
            return result;
        },

    updateConfirmed:
        (
            id: string,
            model: UpdateReservationModel
        ): AppThunkActionAsync<ReservationsAction, UpdateConfirmedResult> =>
        async (dispatch, getState) => {
            const result = await Reservations.updateConfirmed(id, model);
            if (result.status === 'updated') {
                actionCreators.applyRealtimeUpdate(result.reservation)(dispatch, getState);
            }
            return result;
        },

    updateSeated:
        (
            id: string,
            model: UpdateReservationModel
        ): AppThunkActionAsync<ReservationsAction, UpdateSeatedResult> =>
        async (dispatch, getState) => {
            const changeRevision = getState?.().reservations.changeRevision;
            const result = await Reservations.updateSeated(id, model);
            if (result.status === 'updated') {
                let reservation = result.reservation;
                if (getState && getState().reservations.changeRevision !== changeRevision) {
                    let revision = getState().reservations.changeRevision;
                    do {
                        reservation = await getCurrentReservation(id);
                        const nextRevision = getState().reservations.changeRevision;
                        if (nextRevision === revision) break;
                        revision = nextRevision;
                    } while (true);
                }
                actionCreators.applyRealtimeUpdate(reservation)(dispatch, getState);
                return { ...result, reservation };
            }
            return result;
        },

    createReservation:
        (
            model: UpdateReservationModel
        ): AppThunkActionAsync<ReservationsAction, CreateReservationResult> =>
        async (dispatch) => {
            const result = await Reservations.createReservation(model);
            if (result.status === 'created') {
                activeRevision++;
                dispatch({ type: 'RESERVATION_CONFIRMED', reservation: result.reservation });
            }
            return result;
        },

    markNoShow: (id: string): AppThunkActionAsync<ReservationsAction, void> =>
        saveFinalStatus(id, (reservationId) =>
            Reservations.changeStatus(reservationId, 'NoShow')
        ),

    completeSeated: (
        id: string,
        expectedVersion?: number
    ): AppThunkActionAsync<ReservationsAction, void> =>
        saveFinalStatus(id, (reservationId) =>
            Reservations.changeStatus(reservationId, 'Completed', expectedVersion)
        ),

    cancel:
        (
            id: string,
            model: CancelReservationModel
        ): AppThunkActionAsync<ReservationsAction, CancelReservationResult> =>
        (dispatch, getState) => {
            const pending = cancellationRequests.get(id);
            if (pending) return pending;
            const restaurantId = getState?.().user.currentRestaurantId;
            const isCurrent = () => restaurantId === getState?.().user.currentRestaurantId;
            dispatch({ type: 'RESERVATION_CANCEL_PENDING', id, isPending: true });
            const request = (async () => {
                try {
                    const result = await Reservations.cancel(id, model);
                    if (!isCurrent()) return result;
                    if (result.status === 'cancelled') {
                        if (getState?.().reservations.selectedActiveId === id) {
                            dispatch({ type: 'RESERVATION_ACTIVE_SELECT', id: null });
                        }
                        actionCreators.applyRealtimeUpdate(result.reservation)(dispatch, getState);
                    } else if (result.status === 'alreadyHandled') {
                        const current = await getCurrentReservation(id);
                        if (isCurrent()) {
                            actionCreators.applyRealtimeUpdate(current, true)(dispatch, getState);
                        }
                    }
                    return result;
                } finally {
                    if (isCurrent()) {
                        dispatch({ type: 'RESERVATION_CANCEL_PENDING', id, isPending: false });
                    }
                }
            })();
            cancellationRequests.set(id, request);
            const clear = () => {
                if (cancellationRequests.get(id) === request) cancellationRequests.delete(id);
            };
            request.then(clear, clear);
            return request;
        },

    applyRealtimeUpdate:
        (
            reservation: ReservationDTO,
            keepActiveDetails = false
        ): AppThunkAction<ReservationsAction> =>
        (dispatch, getState) => {
            if (getState && getState().user.currentRestaurantId !== reservation.restaurantId) {
                return;
            }
            if (
                getState?.().reservations.requestDialogId === reservation.id &&
                (reservation.status !== 'Requested' || reservation.isArchived) &&
                !requestClosures.has(reservation.id)
            ) {
                dispatch({ type: 'RESERVATION_REQUEST_DIALOG', id: null });
                globalStore.actionCreators.showToaster(
                    'error',
                    i18n.t('reservations.alreadyHandled')
                )(dispatch);
            }
            activeRevision++;
            requestsRevision++;
            dispatch({
                type: 'RESERVATION_REALTIME_UPDATE',
                reservation,
                ...(keepActiveDetails ? { keepActiveDetails: true } : {}),
            });
            const key = getState?.().reservations.availability.key;
            if (key) {
                const [start, duration, excludeId] = key.split('|');
                actionCreators.getTablesAvailability(
                    start,
                    Number(duration),
                    excludeId || null
                )(dispatch, getState);
            }
        },
};
